import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LoaderCircle, Mountain, HardHat, Calculator, ShieldCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, register, verifyOtp, resendOtp, forgotPassword, formatApiErrorDetail } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [otp, setOtp] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    company_name: "",
    company_rfc: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startCooldown = () => setCooldown(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.twoFactor) {
          setPendingEmail(res.email);
          setStep("otp");
          setOtp("");
          startCooldown();
          toast.success("Código enviado a tu correo");
          return;
        }
      } else {
        const res = await register(form);
        if (res?.twoFactor) {
          setPendingEmail(res.email);
          setStep("otp");
          setOtp("");
          startCooldown();
          toast.success("Código enviado a tu correo");
          return;
        }
      }
      toast.success("Bienvenido a EMAURAN");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(pendingEmail, otp.trim());
      toast.success("Bienvenido a EMAURAN");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await resendOtp(pendingEmail);
      startCooldown();
      toast.success("Te enviamos un nuevo código");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await forgotPassword(form.email);
      toast.success(res?.message || "Revisa tu correo");
      setMode("login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative border-r border-border"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,18,36,0.42), rgba(10,18,36,0.78)), url(/brand/login_hero.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <img src="/brand/emauran.png" alt="EMAURAN by OOMINING" data-testid="login-brand-logo"
          className="h-32 w-auto object-contain" style={{ filter: "brightness(0) invert(1)" }} />

        <div className="text-white/90 space-y-5">
          <h1 className="font-head font-black text-4xl leading-tight tracking-tight">
            Costos, presupuestación y precios unitarios para minería y obra pesada.
          </h1>
          <p className="text-white/60 text-sm max-w-md">
            Catálogos maestros, motor normativo FASAR, análisis de precios unitarios (APU) y costo
            horario de equipo con precisión de 6 decimales. Conforme a obra pública federal mexicana.
          </p>
          <div className="flex gap-6 pt-2">
            <div className="flex items-center gap-2 text-white/80">
              <Mountain className="h-5 w-5 text-primary" /><span className="text-xs uppercase tracking-widest">Minería</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <HardHat className="h-5 w-5 text-primary" /><span className="text-xs uppercase tracking-widest">Construcción</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Calculator className="h-5 w-5 text-primary" /><span className="text-xs uppercase tracking-widest">Costos</span>
            </div>
          </div>
          <div className="flex gap-6 pt-2 font-mono-num text-xs text-white/40 uppercase tracking-widest">
            <span>APU</span>
            <span>FASAR</span>
            <span>WBS</span>
            <span>SFP</span>
          </div>
        </div>

        <p className="font-mono-num text-[11px] text-white/40">EMAURAN · by OOMINING · Mine Management Software</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="w-full max-w-sm relative exec-panel p-7 sm:p-8 shadow-[0_12px_40px_-12px_rgba(10,18,36,0.25)] exec-rise">
          <div className="h-1 w-12 bg-copper mb-5 rounded-sm" />
          <div className="lg:hidden mb-8">
            <img src="/brand/emauran.png" alt="EMAURAN by OOMINING" className="h-20 w-auto object-contain dark:[filter:brightness(0)_invert(1)]" />
          </div>

          <h2 className="font-head font-black text-2xl tracking-tight uppercase">
            {step === "otp"
              ? "Verificación en dos pasos"
              : mode === "forgot" ? "Recuperar contraseña"
              : mode === "login" ? "Iniciar sesión" : "Crear cuenta y empresa"}
          </h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {step === "otp"
              ? `Ingresa el código de 6 dígitos que enviamos a ${pendingEmail}.`
              : mode === "forgot"
              ? "Te enviaremos un enlace para crear una contraseña nueva."
              : mode === "login"
              ? "Accede a tus catálogos y presupuestos."
              : "Registra tu empresa. Serás el administrador."}
          </p>

          {step === "otp" ? (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              <div className="flex items-center gap-2 text-primary">
                <ShieldCheck className="h-5 w-5" />
                <span className="text-xs uppercase tracking-widest">Código de verificación</span>
              </div>
              <Input
                data-testid="otp-input"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="––––––"
                required
                className="text-center text-2xl tracking-[0.5em] font-mono-num placeholder:text-muted-foreground/40"
              />
              <p className="text-[11px] text-muted-foreground" data-testid="otp-expiry-note">
                El código vence en 10 minutos. Revisa tu bandeja de entrada y spam.
              </p>
              <Button
                data-testid="otp-submit"
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full rounded-sm"
              >
                {loading && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
                Verificar y entrar
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  data-testid="otp-back"
                  onClick={() => { setStep("credentials"); setOtp(""); }}
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" /> Volver
                </button>
                <button
                  type="button"
                  data-testid="otp-resend"
                  onClick={resend}
                  disabled={cooldown > 0}
                  className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
                </button>
              </div>
            </form>
          ) : mode === "forgot" ? (
            <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-form">
              <div>
                <Label className="text-xs">Correo</Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  required
                  className="mt-1"
                />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full rounded-sm mt-2">
                {loading && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
                Enviar enlace
              </Button>
              <button
                type="button"
                data-testid="forgot-back"
                onClick={() => setMode("login")}
                className="mt-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Volver a iniciar sesión
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs">Nombre completo</Label>
                  <Input data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Empresa</Label>
                  <Input
                    data-testid="register-company"
                    value={form.company_name}
                    onChange={set("company_name")}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">RFC (opcional)</Label>
                  <Input data-testid="register-rfc" value={form.company_rfc} onChange={set("company_rfc")} className="mt-1 font-mono-num" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs">Correo</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Contraseña</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                className="mt-1"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm mt-2"
            >
              {loading && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Entrar" : "Crear cuenta"}
            </Button>
            {mode === "register" && (
              <p className="text-[11px] text-muted-foreground pt-1" data-testid="password-hint">
                Mínimo 8 caracteres con mayúscula, minúscula, número y símbolo.
              </p>
            )}
          </form>

          {mode === "login" && (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setMode("forgot")}
              className="mt-3 block text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}

          <button
            data-testid="toggle-auth-mode"
            onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
            className="mt-5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {mode === "login"
              ? "¿No tienes cuenta? Registra tu empresa"
              : "¿Ya tienes cuenta? Inicia sesión"}
          </button>
          </>
          )}
          <div className="mt-6 pt-4 border-t border-border flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Acceso cifrado · 2FA obligatorio · Sesiones auditadas
          </div>
        </div>
      </div>
    </div>
  );
}
